import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import { useUserRolePermissions } from 'hooks';
import TrailerStatusFiltersDrawer from '../components/TrailerStatusFiltersDrawer';

const CardsDrawer = ({
  backlabel,
  children,
  classes,
  onCreateTrailer,
  onViewTrailer,
  setTitle,
  setCardsFilters,
  cardsFilters,
  onResetFilters,
  ...forwardedProps
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const { isDispatchingManage } = useUserRolePermissions();
  const { viewBy, viewById } = useParams();
  const navigate = useNavigate();

  const showMoreButton = viewBy === 'trailer';
  const menuAnchorRef = useRef(null);

  const openFilters = () => {
    setFiltersOpen(true);
  };
  const closeFilters = () => {
    setFiltersOpen(false);
  };
  const handleBackClick = () => {
    setTitle('');
    navigate(`../compliance/trailer-status/${viewBy}`);
  };

  const handleViewButtonClick = () => {
    onViewTrailer(viewById);
  };

  const handleAddTrailerButtonClick = () => {
    onCreateTrailer();
  };

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      {...forwardedProps}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'surface.light',
            border: 0,
            height: theme => `calc(100% - ${theme.spacing(7)})`,
            top: theme => theme.spacing(7),
            width: '100%',
          },
        },
      }}
    >
      <>
        <Toolbar
          disableGutters
          sx={{
            backgroundColor: 'transparent',
            boxShadow: 'rgba(0, 0, 0, 0.16) 0px 0px 8px 4px',
            color: 'secondary.main',
            minHeight: theme => theme.spacing(7),
            width: '100%',
            zIndex: 4,
          }}
        >
          {showMoreButton ? (
            <MenuItem
              onClick={() => setMenuOpen(!menuOpen)}
              sx={{
                backgroundColor: 'surface.white',
                color: 'secondary.main',
                display: 'flex',
                height: 56,
                justifyContent: 'center',
                margin: 0,
                width: '100%',
              }}
            >
              <Box alignItems="center" display="flex" ref={menuAnchorRef}>
                More
                <ArrowDropDownRoundedIcon />
              </Box>
              <Menu
                anchorEl={menuAnchorRef?.current}
                onClose={() => setMenuOpen(false)}
                open={menuOpen}
              >
                <MenuItem
                  onClick={() => {
                    handleViewButtonClick();
                    setMenuOpen(false);
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  View Trailer
                </MenuItem>
                {isDispatchingManage && (
                  <MenuItem
                    onClick={() => {
                      handleAddTrailerButtonClick();
                      setMenuOpen(false);
                    }}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    New Trailer
                  </MenuItem>
                )}
              </Menu>
            </MenuItem>
          ) : (
            isDispatchingManage && (
              <MenuItem
                onClick={() => setMenuOpen(!menuOpen)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'secondary.main',
                  },
                  backgroundColor: 'secondary.main',
                  color: 'surface.white',
                  display: 'flex',
                  height: 56,
                  justifyContent: 'center',
                  margin: 0,
                  width: '100%',
                }}
              >
                <Box
                  alignItems="center"
                  display="flex"
                  onClick={() => {
                    handleAddTrailerButtonClick();
                    setMenuOpen(false);
                  }}
                >
                  <AddIcon />
                  New Trailer
                </Box>
              </MenuItem>
            )
          )}
          <Divider orientation="vertical" />
          <MenuItem
            onClick={openFilters}
            sx={{
              backgroundColor: 'surface.white',
              color: 'secondary.main',
              display: 'flex',
              height: 56,
              justifyContent: 'center',
              margin: 0,
              width: '100%',
            }}
          >
            Filters <ChevronRightRoundedIcon />
          </MenuItem>
        </Toolbar>

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            margin: '0 auto',
            maxWidth: 948,
            padding: theme => theme.spacing(2, 3, 1),
            width: '100%',
          }}
        >
          <Button
            onClick={handleBackClick}
            sx={{
              color: 'secondary.main',
              fontSize: 12,
              justifyContent: 'flex-start',
              margin: theme => `0 ${theme.spacing(3)} ${theme.spacing(0.5)} ${theme.spacing(-1)}`,
              marginRight: { sm: theme => theme.spacing(3), xs: 0 },
              padding: 0,
              textTransform: 'capitalize',
              typography: 'button',
            }}
            variant="text"
          >
            <ChevronLeftRoundedIcon />
            {backlabel}
          </Button>
        </Box>
        <Box
          sx={{
            '& > *': {
              marginBottom: theme => theme.spacing(3),
              marginLeft: theme => theme.spacing(3),
            },
            alignContent: 'start',
            backgroundColor: 'surface.light',
            display: 'flex',
            flexWrap: 'wrap',
            height: '100%',
            margin: '0 auto',
            overflow: 'auto',
            paddingLeft: theme => theme.spacing(1),
            paddingRight: theme => theme.spacing(1),
            width: '100%',
          }}
        >
          {children}
        </Box>
        {createPortal(
          <TrailerStatusFiltersDrawer
            cardsFilters={cardsFilters}
            onClose={closeFilters}
            onResetFilters={onResetFilters}
            open={filtersOpen}
            setCardsFilters={setCardsFilters}
          />,
          document.body
        )}
      </>
    </Drawer>
  );
};

CardsDrawer.propTypes = {
  backlabel: PropTypes.string.isRequired,
  cardsFilters: PropTypes.shape({}).isRequired,
  children: PropTypes.node.isRequired,
  classes: PropTypes.shape({}),
  onCreateTrailer: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onViewTrailer: PropTypes.func,
  setCardsFilters: PropTypes.func.isRequired,
  setTitle: PropTypes.func,
};

CardsDrawer.defaultProps = {
  classes: {},
  onViewTrailer: undefined,
  setTitle: () => {},
};

export default CardsDrawer;
